import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { PRIORITY_LEVEL_LABELS_FR, REPORT_STATUS_LABELS_FR, SOURCE_CHANNEL_LABELS_FR, } from '@ci-ville/shared';
const BRAND = [33, 150, 243];
const MARGIN = 15;
const PAGE_W = 210;
const PAGE_H = 297;
const CONTENT_W = PAGE_W - MARGIN * 2;
const fmt = (d) => (d ? format(new Date(d), "d MMMM yyyy 'à' HH'h'mm", { locale: fr }) : '');
async function toDataUrl(url) {
    try {
        const res = await fetch(url);
        if (!res.ok)
            return null;
        const blob = await res.blob();
        const data = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = reject;
            r.readAsDataURL(blob);
        });
        const { w, h } = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => resolve({ w: 4, h: 3 });
            img.src = data;
        });
        return { data, w, h };
    }
    catch {
        return null;
    }
}
/**
 * Fiche PDF d'un signalement (reprise de la v1), générée en texte vectoriel.
 * Les commentaires internes ne sont inclus que si demandé.
 */
export async function exportReportSheetPDF(report, opts) {
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = 0;
    const ensure = (h) => {
        if (y + h > PAGE_H - 18) {
            pdf.addPage();
            y = MARGIN;
        }
    };
    const heading = (text) => {
        ensure(12);
        y += 4;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(...BRAND);
        pdf.text(text.toUpperCase(), MARGIN, y);
        pdf.setDrawColor(220, 228, 240);
        pdf.line(MARGIN, y + 1.5, PAGE_W - MARGIN, y + 1.5);
        y += 6;
        pdf.setTextColor(30, 30, 30);
    };
    const paragraph = (text, size = 9.5) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(size);
        const lines = pdf.splitTextToSize(text || 'Aucun texte', CONTENT_W);
        for (const line of lines) {
            ensure(5);
            pdf.text(line, MARGIN, y);
            y += size * 0.45;
        }
    };
    // En-tête
    pdf.setFillColor(...BRAND);
    pdf.rect(0, 0, PAGE_W, 26, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.text('CI-VILLE', MARGIN, 11);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(opts.tenantName, MARGIN, 17);
    pdf.text('Fiche de signalement', MARGIN, 22);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(report.referenceNumber, PAGE_W - MARGIN, 12, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text(`Générée le ${fmt(new Date().toISOString())}`, PAGE_W - MARGIN, 18, { align: 'right' });
    y = 36;
    pdf.setTextColor(20, 20, 20);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    for (const line of pdf.splitTextToSize(report.title, CONTENT_W)) {
        pdf.text(line, MARGIN, y);
        y += 6;
    }
    // Tableau d'informations
    heading('Informations');
    const rows = [
        ['Statut', REPORT_STATUS_LABELS_FR[report.status] ?? report.status],
        ['Priorité', `${PRIORITY_LEVEL_LABELS_FR[report.priorityLevel] ?? report.priorityLevel}${report.priorityScore != null ? ` (score ${report.priorityScore}/100)` : ''}`],
        ['Catégorie', report.category?.nameFr ?? 'Aucune'],
        ['Types de problème', (report.subcategoryLinks ?? []).map((l) => l.subcategory.nameFr).join(', ') || 'Aucun'],
        ['Reçu par', SOURCE_CHANNEL_LABELS_FR[report.sourceChannel] ?? report.sourceChannel ?? 'Non précisé'],
        ['Créé le', fmt(report.createdAt)],
        ['Échéance (SLA)', report.slaTargetAt ? `${fmt(report.slaTargetAt)}${['OPEN', 'IN_PROGRESS'].includes(report.status) && new Date(report.slaTargetAt) < new Date() ? ' (dépassée)' : ''}` : 'Aucune'],
        ['Résolu le', fmt(report.resolvedAt)],
        ['Assigné à', report.assignedTo ? `${report.assignedTo.firstName} ${report.assignedTo.lastName}` : 'Non assigné'],
        ['Adresse', report.address ?? 'Non précisée'],
        ['Coordonnées', report.latitude != null ? `${Number(report.latitude).toFixed(6)}, ${Number(report.longitude).toFixed(6)}` : 'Non précisées'],
    ];
    if (report.citizenName || report.citizenPhone || report.citizenEmail) {
        rows.push(['Signalé par', [report.citizenName, report.citizenPhone, report.citizenEmail].filter(Boolean).join(' · ')]);
    }
    pdf.setFontSize(9);
    for (const [k, v] of rows) {
        const lines = pdf.splitTextToSize(v, CONTENT_W - 45);
        ensure(lines.length * 4.5 + 1);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(100, 110, 125);
        pdf.text(k, MARGIN, y);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(20, 20, 20);
        lines.forEach((l, i) => pdf.text(l, MARGIN + 45, y + i * 4.5));
        y += lines.length * 4.5 + 1;
    }
    heading('Description');
    paragraph(report.description);
    if (report.status === 'RESOLVED' && report.resolutionNotes) {
        heading('Résolution');
        paragraph(report.resolutionNotes);
    }
    if (report.status === 'REJECTED' && report.rejectedReason) {
        heading('Motif du rejet');
        paragraph(report.rejectedReason);
    }
    // Photos (2 par ligne)
    const photoUrls = [...(report.photoUrls ?? []), ...(report.resolutionPhotoUrls ?? [])];
    if (photoUrls.length) {
        heading(`Photos (${photoUrls.length})`);
        const images = (await Promise.all(photoUrls.map(toDataUrl))).filter(Boolean);
        const cellW = (CONTENT_W - 6) / 2;
        for (let i = 0; i < images.length; i += 2) {
            const pair = images.slice(i, i + 2);
            const heights = pair.map((im) => Math.min(70, (cellW * im.h) / im.w));
            const rowH = Math.max(...heights);
            ensure(rowH + 4);
            pair.forEach((im, j) => {
                const h = heights[j];
                const w = (h * im.w) / im.h;
                pdf.addImage(im.data, 'JPEG', MARGIN + j * (cellW + 6), y, w, h);
            });
            y += rowH + 4;
        }
        if (images.length < photoUrls.length)
            paragraph(`${photoUrls.length - images.length} photo(s) non chargée(s).`, 8);
    }
    // Historique
    const history = [...(report.statusHistory ?? [])].reverse();
    if (history.length) {
        heading('Historique');
        for (const ev of history) {
            const label = `${fmt(ev.createdAt)} : ${ev.fromStatus ? `${REPORT_STATUS_LABELS_FR[ev.fromStatus]} → ` : ''}${REPORT_STATUS_LABELS_FR[ev.toStatus]}${ev.note ? ` (${ev.note})` : ''}`;
            paragraph(label, 9);
        }
    }
    // Commentaires
    const comments = (report.comments ?? []).filter((c) => opts.includeInternal || !c.isInternal).reverse();
    if (comments.length) {
        heading(opts.includeInternal ? 'Commentaires' : 'Commentaires publics');
        for (const c of comments) {
            ensure(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8.5);
            pdf.setTextColor(100, 110, 125);
            pdf.text(`${c.author.firstName} ${c.author.lastName ?? ''} · ${fmt(c.createdAt)}${c.isInternal ? ' · interne' : ''}`, MARGIN, y);
            y += 4;
            pdf.setTextColor(20, 20, 20);
            paragraph(c.body, 9);
            y += 1.5;
        }
    }
    // Pied de page
    const pages = pdf.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(130, 130, 130);
        pdf.text(`${report.referenceNumber} · page ${i} / ${pages}`, PAGE_W / 2, PAGE_H - 7, { align: 'center' });
        pdf.text(opts.includeInternal ? 'Document interne : contient des renseignements personnels (Loi 25)' : 'CI-VILLE par GEOSIRA INC.', MARGIN, PAGE_H - 7);
    }
    pdf.save(`fiche-${report.referenceNumber}.pdf`);
}
//# sourceMappingURL=report-pdf.js.map